import { serverUrl } from '@utils/MkEnv'
import { API } from '@constants'
import { themeBrandToPageContentBrand } from '@account/services/authLabels'
import type { NavMenuItem } from './Header'

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0
}

function isNavLevel(value: unknown): value is '1' | '2' | '3' {
  return value === '1' || value === '2' || value === '3'
}

type AemTopNavMenuItem = {
  level?: unknown
  dataTestId?: unknown
  label?: unknown
  href?: unknown
  children?: unknown
}

type AemTopNavResponse = {
  content?: {
    menuItems?: unknown
  }
}

const TOP_NAV_CACHE_KEY_PREFIX = 'mk-top-nav:'

function cacheKeyForBrand(brand: string): string {
  return `${TOP_NAV_CACHE_KEY_PREFIX}${brand}`
}

function getCachedMenuItems(brand: string): NavMenuItem[] | null {
  if (typeof window === 'undefined' || !window.sessionStorage) return null

  try {
    const raw = window.sessionStorage.getItem(cacheKeyForBrand(brand))
    if (!raw) return null

    const parsed = JSON.parse(raw) as unknown
    return Array.isArray(parsed) ? (parsed as NavMenuItem[]) : null
  } catch {
    window.sessionStorage.removeItem(cacheKeyForBrand(brand))
    return null
  }
}

function setCachedMenuItems(brand: string, items: NavMenuItem[]): void {
  if (typeof window === 'undefined' || !window.sessionStorage || items.length === 0) return

  try {
    window.sessionStorage.setItem(cacheKeyForBrand(brand), JSON.stringify(items))
  } catch {
    // no-op
  }
}

function mapMenuItems(items: unknown): NavMenuItem[] {
  if (!Array.isArray(items)) return []

  return items.reduce<NavMenuItem[]>((acc, item) => {
    const candidate = (item || {}) as AemTopNavMenuItem
    const label = hasText(candidate.label) ? candidate.label.trim() : ''
    const href = hasText(candidate.href) ? candidate.href.trim() : ''
    const dataTestId = hasText(candidate.dataTestId) ? candidate.dataTestId.trim() : ''
    const level = isNavLevel(candidate.level) ? candidate.level : '1'

    if (!hasText(label) || !hasText(href) || !hasText(dataTestId)) {
      return acc
    }

    const children = mapMenuItems(candidate.children)

    acc.push({
      label,
      href,
      level,
      dataTestId,
      ...(children.length > 0 ? { children } : {}),
    })

    return acc
  }, [])
}

export async function getTopNavMenuItems(themeBrand: string): Promise<NavMenuItem[] | null> {
  const normalizedBrand = themeBrandToPageContentBrand(themeBrand)
  if (!normalizedBrand) return null

  const cachedMenuItems = getCachedMenuItems(normalizedBrand)
  if (cachedMenuItems && cachedMenuItems.length > 0) {
    return cachedMenuItems
  }

  try {
    const params = new URLSearchParams({ brand: normalizedBrand })
    const baseUrl = `${serverUrl}`.replace(/\/+$/, '')
    const apiPath = `${API}`.replace(/^\/+/, '')
    const requestUrl = `${baseUrl}/${apiPath}/aem/get-top-navigation?${params.toString()}`
    const response = await fetch(requestUrl, {
      credentials: 'include',
      headers: {
        Accept: 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(`Failed to fetch top navigation: ${response.status}`)
    }

    const payload = (await response.json()) as AemTopNavResponse
    const mappedItems = mapMenuItems(payload?.content?.menuItems)
    if (mappedItems.length > 0) {
      setCachedMenuItems(normalizedBrand, mappedItems)
    }
    return mappedItems.length > 0 ? mappedItems : null
  } catch (error) {
    console.error('Failed to load top navigation menu items from top navigation API', {
      brand: normalizedBrand,
      error,
    })
    return null
  }
}
